import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore } from '@clarix/auth-store';
import { getApiClient } from '@clarix/api-client';
import { useMe } from '@/core/api';
import { unwrap, type ApiResponse } from '@/core/api/envelope';
import { AppBootSplash } from '@/gateway/AppBootSplash';
import { isOwner } from '@/gateway/isOwner';
import { WorkspaceSetupPending } from '@/gateway/WorkspaceSetupPending';
import { useBootTrace } from '@/gateway/devtools/bootTrace';

/**
 * Workspace-subdomain guard. Until `setup_state === 'COMPLETE'`, redirects
 * everything except /setup itself to the chrome-free wizard at /setup.
 *
 * /admin/* is gated too: during initial setup, the wizard is the only
 * reachable surface. Admin pages unlock as soon as setup completes.
 *
 * Apex (no workspace slug) passes through — onboarding owns its own gating.
 */

interface SetupStatus {
  state: 'CONFIGURATION' | 'MASTERS' | 'INVITES' | 'APPS' | 'COMPLETE';
  completed_at: string | null;
}

export function RequireSetupComplete() {
  const location = useLocation();
  const workspaceSlug = useAuthStore((s) => s.workspaceSlug);
  const hasToken = useAuthStore((s) => !!s.token);
  useBootTrace('RequireSetupComplete', { path: location.pathname, workspaceSlug, hasToken });

  // The wizard route passes through unconditionally so the user can reach
  // it. Everything else (including /admin/*) is gated until setup completes.
  // It still needs SetupSlugReconciler: this pass-through is what makes /setup
  // a sibling of WorkspaceShell rather than nested inside it (chrome-free), so
  // WorkspaceShell's own workspaceSlug↔/me correction never runs on this path.
  if (location.pathname.startsWith('/setup')) {
    return hasToken ? <SetupSlugReconciler /> : <Outlet />;
  }
  // One element in this slot with or without workspace context: a slug that arrives mid-boot
  // (token heal, single-host /me) must not swap the element type and remount the chrome below.
  return <CheckSetupAndGate slug={hasToken ? workspaceSlug : null} />;
}

const SETUP_COMPLETE_KEY = 'clarix.setup.complete';

/** Setup never un-completes, so a new tab can trust the last answer and re-verify in the background. */
function readSetupComplete(slug: string): boolean {
  try {
    return localStorage.getItem(`${SETUP_COMPLETE_KEY}:${slug}`) === '1';
  } catch {
    return false;
  }
}

function writeSetupComplete(slug: string, complete: boolean): void {
  try {
    if (complete) localStorage.setItem(`${SETUP_COMPLETE_KEY}:${slug}`, '1');
    else localStorage.removeItem(`${SETUP_COMPLETE_KEY}:${slug}`);
  } catch {
    // Storage unavailable — the gate just checks on every boot.
  }
}

/**
 * A `workspaceSlug` left over from an earlier workspace or session (nothing
 * else on the /setup path ever re-validates it — see the comment above) drives
 * every wizard call at a workspace this session isn't even in: `/workspaces/
 * <stale-slug>/setup`, `/admin/masters/import`, all of it 404 "Workspace not
 * found" with no indication why. Mirrors WorkspaceShell's own `/me` correction
 * so /setup self-heals the same way, without giving up the always-reachable
 * bypass above (renders immediately; corrects in the background).
 */
function SetupSlugReconciler() {
  const workspaceSlug = useAuthStore((s) => s.workspaceSlug);
  const setWorkspaceSlug = useAuthStore((s) => s.setWorkspaceSlug);
  const me = useMe({ activeSlug: workspaceSlug ?? undefined });

  useEffect(() => {
    const active = me.data?.active_workspace_slug;
    if (!active || active === workspaceSlug) return;
    setWorkspaceSlug(active);
  }, [me.data?.active_workspace_slug, workspaceSlug, setWorkspaceSlug]);

  return <Outlet />;
}

/** `slug` is null with no workspace context (apex or pre-auth), which is never gated here. */
function CheckSetupAndGate({ slug }: { slug: string | null }) {
  const user = useAuthStore((s) => s.user);
  const owner = isOwner(user);
  // GET /workspaces/:slug/setup requires OWNER or ADMIN — for plain members
  // the call can only 403, so don't make it at all.
  const canReadSetup = owner || user?.workspace_role === 'ADMIN';
  const shouldCheck = !!slug && canReadSetup;
  const [result, setResult] = useState<{ slug: string; complete: boolean } | null>(null);
  // Once through, stay through while a later check runs: dropping back to the
  // loader would unmount the chrome and the app underneath it.
  const [passed, setPassed] = useState(false);

  useEffect(() => {
    if (!slug || !shouldCheck) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await getApiClient().get<ApiResponse<SetupStatus>>(`/workspaces/${slug}/setup`);
        const data = unwrap(res.data);
        if (cancelled) return;
        const complete = data.state === 'COMPLETE';
        writeSetupComplete(slug, complete);
        setResult({ slug, complete });
      } catch {
        // If we can't determine setup state (network blip, race, etc.) we
        // err on the side of letting the user through rather than locking
        // them out. The next API call they make will surface the real error.
        if (!cancelled) setResult({ slug, complete: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, shouldCheck]);

  let status: 'loading' | 'ok' | 'redirect';
  if (!slug || !shouldCheck) status = 'ok';
  else if (result?.slug === slug) status = result.complete ? 'ok' : 'redirect';
  else status = passed || readSetupComplete(slug) ? 'ok' : 'loading';

  useBootTrace('CheckSetupAndGate', { slug, status });
  if (status === 'ok' && !passed) setPassed(true);

  if (status === 'loading') return <AppBootSplash />;
  if (status === 'redirect') {
    // Owners are sent to finish the setup wizard; members can't enter it, so
    // they see a holding screen until setup completes.
    return owner ? <Navigate to="/setup" replace /> : <WorkspaceSetupPending />;
  }
  return <Outlet />;
}
